import { ErrorPage } from '@/components/errors';
import { env } from '@/env';
import { useGetPersonalInfo } from '@/hooks/api';
import { useSendCode } from '@/hooks/api/contact-validation.hooks';
import { ModuleName, Screen, templates, type SkinPageConfig } from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useRemoveMfa } from './-hooks/remove-mfa.hook';
import { useSetupMfa } from './-hooks/setup-mfa.hook';
import { useRemoveTwoFactor } from './-hooks/remove-2fa.hook';
import { type MfaSetupFlowData } from './-model/flow';
import { type SecurityProps } from './-model/props';
import { SecurityLight } from './-skins';

export const Route = createFileRoute('/(security)/security')({
  component: RouteComponent,
  errorComponent: ErrorPage,
  staticData: {
    module: ModuleName.Security,
    screen: Screen.LandingPage,
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const [mfaAlertOpen, setMfaAlertOpen] = useState(false);
  const { data: personalInfo, isLoading: personalInfoLoading } = useGetPersonalInfo();
  const { mutate, isPending: removeTwoFactorLoading } = useRemoveTwoFactor();
  const { mutate: removeMfa, isPending: removeMfaLoading } = useRemoveMfa();
  const { mutateAsync: setupMfa, isPending: setupMfaLoading } = useSetupMfa();
  const { isSending, captcha, sendCodeAsync } = useSendCode();
  const phoneNumber = personalInfo?.UsernameAliases.find((al) => al.Type === 'Mobile');
  const email = personalInfo?.UsernameAliases.find((al) => al.Type === 'Email');
  const twoFactorActive = !!personalInfo?.TwoFactorAuthEnabled;
  const canChangeTwoFactor = env.TwoFactorAuthenticationEnabled && !!phoneNumber?.Username;
  const mfaActive = !!personalInfo?.Mfa?.Enabled;
  const props: SecurityProps = {
    phone: phoneNumber?.Username,
    email: email?.Username,
    twofactorActive: twoFactorActive || !canChangeTwoFactor,
    canChangeTwoFactor: canChangeTwoFactor,
    isLoading: personalInfoLoading,
    changeTwoFactorLoading: removeTwoFactorLoading || isSending,
    captcha,
    mfaActive: mfaActive,
    mfaDevice: personalInfo?.Mfa?.Device,
    mfaEnabledAt: personalInfo?.Mfa?.EnabledAt,
    mfaRecoveryCodesRemaining: personalInfo?.Mfa?.RecoveryCodesRemaining,
    mfaAlertOpen,
    setMfaAlertOpen,
    removeMfaLoading,
    removeMfa: () => removeMfa(void 0, { onSuccess: () => setMfaAlertOpen(false) }),
    changeMfaLoading: setupMfaLoading || removeMfaLoading,
    changeMfa: () => {
      if (setupMfaLoading || removeMfaLoading) return;

      if (mfaActive) {
        setMfaAlertOpen(true);
        return;
      }

      setupMfa().then((setup) => {
        const state: MfaSetupFlowData = setup;
        navigate({ to: '/security/authenticator-app/download-app', state });
      });
    },
    changeTwoFactor: () => {
      if (!canChangeTwoFactor || removeTwoFactorLoading || isSending) return;

      if (twoFactorActive) {
        mutate();
        return;
      }

      if ((captcha.challenge && !captcha.token) || !phoneNumber?.Username) return;

      sendCodeAsync({ targetId: phoneNumber?.Username, type: 'Sms', captcha }).then(
        ({ Success }) => {
          if (!Success) return;
          navigate({
            to: '/security/two-factor-code',
            state: {
              target: phoneNumber?.Username,
            },
          });
        },
      );
    },
  };
  return templates.getSkinSelector(skinConfig(props));
}
function skinConfig(props: SecurityProps): SkinPageConfig {
  return {
    skins: [],
    defaultValue: <SecurityLight {...props} />,
  };
}
